import { ActionTree, ActionContext } from 'vuex';
import { computed } from 'vue';
import { RootState, useStore } from '@/store';
import { State } from './state';
import { Mutations } from './mutations';
import { AuthActionTypes } from './action-types';
import { UserLogged } from '@/models/auth/user-logged.model';
import { AuthMutationTypes } from './mutation-types';
import { Buffer } from 'buffer';
import { useOidcStore } from 'vue3-oidc';
import usuarioService from '@/services/seguranca/seg-usuario.service';
import utlTipoFornecimentoService from '@/services/utilidade/utl-tipo-fornecimento.service';
import * as CryptoJS from 'crypto-js';
import { MenuActionTypes } from '../menu/action-types';
import { LoaderActionTypes } from '../loader/action-types';
import { serviceNotificacao } from '@/services/outros/seg-notificacao.service';
import { MessageActionTypes } from '../messages/action-types';
import { start } from '../../signalr';

const { state } = useOidcStore();
const userManager = computed(() => state.value.userManager);

type AugmentedActionContext = {
  commit<K extends keyof Mutations>(key: K, payload: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
  [AuthActionTypes.ACTION_LOGIN]({ commit, dispatch, getters }: AugmentedActionContext): Promise<boolean>;
  [AuthActionTypes.ACTION_LOGOUT]({ commit }: AugmentedActionContext, user: UserLogged): void;
  [AuthActionTypes.ACTION_LOGIN_LEGACY]({ commit }: AugmentedActionContext, jwtToken: string): Promise<any>;
  [AuthActionTypes.ACTION_LOGOUT_LEGACY]({ commit }: AugmentedActionContext): void;
  [AuthActionTypes.ACTION_UPDATE_USUARIO_ID]({ commit }: AugmentedActionContext): Promise<void>;
}

export const actions: ActionTree<State, RootState> & Actions = {
  async [AuthActionTypes.ACTION_LOGIN]({ commit, dispatch, getters }): Promise<boolean> {
    try {
      dispatch(LoaderActionTypes.ACTION_SHOW);

      if (getters.getContratosUsuario.length <= 0) {
        try {
          const contratos = await usuarioService.getContratosAcessoUsuario();
          await dispatch(MenuActionTypes.ACTION_SET_CONTRATOS, contratos.data);
        } catch {
          await dispatch(MenuActionTypes.ACTION_SET_CONTRATOS, []);
        }
      }

      await usuarioService.verificaUsuarioIntegrado();

      const result = await serviceNotificacao.listByUser();
      start();
      dispatch(MessageActionTypes.SET_MESSAGES, result);

      const resultObra = await usuarioService.getObrasAcessoUsuario();
      dispatch(MenuActionTypes.ACTION_SET_OBRAS, resultObra.data);

      const user = await usuarioService.getSegUsuarioByToken();
      const permissions = user.segUsuarioId ? await usuarioService.getPermissoesSegUsuario() : [];
      const permissionsEncrypted = user.segUsuarioId ? CryptoJS.AES.encrypt(JSON.stringify(permissions), import.meta.env.VITE_APP_KEY as string).toString() : '';
      const grupoUsuarioIdEncrypted = user.segUsuarioId ? CryptoJS.AES.encrypt(user.segGrupoUsuarioId.toString(), import.meta.env.VITE_APP_KEY as string).toString() : '';

      let tiposFornecimentoAtivos: number[] = [];
      try {
        tiposFornecimentoAtivos = await utlTipoFornecimentoService.getTiposFornecimentoAtivos();
      } catch {
        tiposFornecimentoAtivos = [];
      }

      commit(AuthMutationTypes.LOGIN_SUCCESS, { segUsuarioId: user.segUsuarioId, code: permissionsEncrypted, grupoUsuario: grupoUsuarioIdEncrypted, tiposFornecimentoAtivos });

      return Promise.resolve(true);
    } finally {
      dispatch(LoaderActionTypes.ACTION_HIDE);
    }
  },
  [AuthActionTypes.ACTION_LOGOUT]({ commit }) {
    //authService.logout();
    commit(AuthMutationTypes.LOGOUT, undefined);
    userManager.value?.signoutRedirect();
  },
  [AuthActionTypes.ACTION_LOGOUT_LEGACY]() {
    sessionStorage.clear();
    localStorage.clear();
  },
  async [AuthActionTypes.ACTION_UPDATE_USUARIO_ID]({ commit }): Promise<void> {
    try {
      const user = await usuarioService.getSegUsuarioByToken();
      commit(AuthMutationTypes.UPDATE_USUARIO_ID, user.segUsuarioId);
    } catch (error) {
      console.error('Erro ao atualizar usuarioIntegracaoId:', error);
    }
  },
  async [AuthActionTypes.ACTION_LOGIN_LEGACY]({ commit }, jwtToken: string): Promise<any> {
    const user = parseJwt(jwtToken);
    sessionStorage.setItem('isLegacy', '1');
    sessionStorage.setItem('tokenLegacy', jwtToken);
    const permissions = await usuarioService.getPermissoesSegUsuario();
    const permissionsEncrypted = CryptoJS.AES.encrypt(JSON.stringify(permissions), import.meta.env.VITE_APP_KEY as string).toString();
    const grupoUsuarioIdEncrypted = CryptoJS.AES.encrypt(user.codigoGrupoUsuario.toString(), import.meta.env.VITE_APP_KEY as string).toString();
    let tiposFornecimentoAtivos: number[] = [];
    try {
      tiposFornecimentoAtivos = await utlTipoFornecimentoService.getTiposFornecimentoAtivos();
    } catch {
      tiposFornecimentoAtivos = [];
    }
    commit(AuthMutationTypes.LOGIN_SUCCESS, { segUsuarioId: user.codigoUsuario, code: permissionsEncrypted, grupoUsuario: grupoUsuarioIdEncrypted, tiposFornecimentoAtivos });
    if (!localStorage.getItem('ultimaObraSelecionada')) {
      return usuarioService.getObrasAcessoUsuario().then((request) => {
        useStore().dispatch(MenuActionTypes.ACTION_SET_OBRA, { obra: request.data[0] });
      });
    }
    return Promise.resolve();
  }
};

function parseJwt(token: string) {
  return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
}
